import React, { useMemo } from 'react';
import { BeveledPanel } from '../FabProcessTool/BeveledPanel';
import {
  getBrushedAluminum,
  getCyanLedMaterial,
  getGraphiteMetal,
  getWhitePaintedMetal,
} from '../FabProcessTool/equipmentMaterials';
import type { FabToolDetail } from '../FabProcessTool/types';
import type { Awp300Layout } from './awp300Layout';

export type Awp300RobotProps = {
  layout: Awp300Layout;
  opacity?: number;
  ledIntensity?: number;
  detail?: FabToolDetail;
};

/**
 * Central vertical robot + multi-axis arm — parked at top until transfer drives motion.
 * Static pose matches reference poster (arm extended toward chamber).
 */
export const Awp300Robot: React.FC<Awp300RobotProps> = ({
  layout,
  opacity = 1,
  ledIntensity = 1,
  detail = 'high',
}) => {
  if (detail === 'low' || detail === 'very-low') return null;

  const graphite = useMemo(() => getGraphiteMetal(opacity), [opacity]);
  const white = useMemo(() => getWhitePaintedMetal(opacity), [opacity]);
  const brushed = useMemo(() => getBrushedAluminum(opacity), [opacity]);
  const cyan = useMemo(() => getCyanLedMaterial(ledIntensity, opacity), [ledIntensity, opacity]);

  const { width, height, depth, pillarTop, chamberY, rackBottom } = layout;
  const railBottom = rackBottom;
  const railTop = pillarTop - height * 0.12;
  const railH = railTop - railBottom;
  const railY = (railTop + railBottom) * 0.5;
  const railR = width * 0.022;
  const parkY = railTop - height * 0.04;

  return (
    <group name="Awp300Robot">
      <mesh position={[0, railY, depth * 0.02]} material={white} castShadow>
        <cylinderGeometry args={[railR, railR * 1.04, railH, 16]} />
      </mesh>
      <mesh position={[0, railY, depth * 0.02]} material={brushed}>
        <cylinderGeometry args={[railR * 0.5, railR * 0.5, railH * 0.98, 12]} />
      </mesh>
      <BeveledPanel
        size={[railR * 0.55, railH * 0.92, railR * 0.35]}
        material={cyan}
        position={[railR * 0.65, railY, depth * 0.04]}
        smoothness={2}
      />

      <group position={[0, parkY, depth * 0.04]}>
        <BeveledPanel
          size={[width * 0.11, height * 0.045, depth * 0.1]}
          material={graphite}
          castShadow
        />
        <BeveledPanel
          size={[width * 0.07, height * 0.028, depth * 0.07]}
          material={brushed}
          position={[0, height * 0.02, 0]}
        />
        <group position={[0, -height * 0.02, depth * 0.05]} rotation={[0.35, 0, 0]}>
          <BeveledPanel
            size={[width * 0.06, height * 0.025, depth * 0.22]}
            material={white}
            position={[0, 0, depth * 0.1]}
          />
          <BeveledPanel
            size={[width * 0.035, height * 0.02, depth * 0.035]}
            material={cyan}
            position={[0, height * 0.012, depth * 0.2]}
          />
        </group>
      </group>

      {/* Guide rails — right interior track */}
      {[0.32, 0.58].map((t, i) => (
        <BeveledPanel
          key={`guide-${i}`}
          size={[width * 0.01, railH * 0.88, depth * 0.04]}
          material={brushed}
          position={[width * 0.36, railY, depth * (0.08 + t * 0.22)]}
        />
      ))}

      {/* Arm reach indicator toward chamber */}
      <BeveledPanel
        size={[width * 0.04, height * 0.018, depth * 0.14]}
        material={brushed}
        position={[0, chamberY + height * 0.02, depth * 0.1]}
        rotation={[0.25, 0, 0]}
      />
    </group>
  );
};

export default Awp300Robot;
